import React, { useState, useEffect } from 'react';
import { 
  ArrowRight, Calendar, Package, 
  Download, Printer, RefreshCw, 
  FileText, Layers, Settings, Users, Activity
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

const MRPPlanDetail = ({ plan, setActiveView, handleViewItem }) => {
  const [activeTab, setActiveTab] = useState('items'); // items, forecast, capacity, logs
  const [planItems, setPlanItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch MRP items for this plan
  useEffect(() => {
    const fetchPlanItems = async () => {
      if (!plan || !plan.plan_id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch MRP items for this plan
        const { data: itemsData, error: itemsError } = await supabase
          .from('mrp_items')
          .select('*')
          .eq('plan_id', plan.plan_id)
          .order('created_at', { ascending: false });

        if (itemsError) {
          console.error('Error fetching MRP items:', itemsError);
          return;
        }

        setPlanItems(itemsData || []);
      } catch (error) {
        console.error('Error fetching MRP items:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlanItems();
  }, [plan]);

  // Helper functions
  const getStatusDetails = (status, type = 'plan') => {
    const statusMap = {
      'active': { name: '執行中', color: '#10B981' },
      'completed': { name: '已完成', color: '#059669' },
      'draft': { name: '草稿', color: '#6B7280' },
      'pending': { name: '待處理', color: '#3B82F6' },
      'delayed': { name: '延遲', color: '#F59E0B' },
      'cancelled': { name: '已取消', color: '#EF4444' }
    };

    return statusMap[status] || { name: '未知', color: '#6B7280' };
  };
  
  const getProductDetails = (productId) => {
    // For now, return a basic product structure
    // In a real implementation, you would fetch product details from Supabase
    return { name: productId || '未知產品', sku: productId || 'N/A' };
  };

  // Early return if plan is null
  if (!plan) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading MRP plan...</div>
      </div>
    );
  }
  
  const status = getStatusDetails(plan.status);
  
  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'items':
        return renderItemsTab();
      case 'forecast':
        return renderForecastTab();
      case 'capacity':
        return renderCapacityTab();
      case 'logs':
        return renderLogsTab();
      default:
        return renderItemsTab();
    }
  };
  
  // Render items tab
  const renderItemsTab = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">物料</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">需求數量</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">可用數量</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">淨需求</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">優先級</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">狀態</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {loading ? (
            <tr>
              <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                Loading MRP items...
              </td>
            </tr>
          ) : planItems.length > 0 ? (
            planItems.map((item) => {
              const itemStatus = getStatusDetails(item.status, 'item');
              const product = getProductDetails(item.product_id);
              return (
                <tr 
                  key={item.id} 
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.required_quantity}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.available_quantity}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.net_requirement}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span 
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${item.priority === 'critical' ? 'bg-red-100 text-red-800' : item.priority === 'high' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}
                    >
                      {item.priority === 'critical' ? '緊急' : item.priority === 'high' ? '高' : '普通'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span 
                      className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" 
                      style={{ backgroundColor: `${itemStatus.color}20`, color: itemStatus.color }}
                    >
                      {itemStatus.name}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button 
                      onClick={() => handleViewItem(item)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      查看
                    </button>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                此MRP計劃沒有物料項目
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
  
  // Render forecast tab
  const renderForecastTab = () => (
    <div className="p-6">
      <div className="bg-gray-50 p-4 rounded-lg mb-4">
        <p className="text-gray-700">此計劃的需求預測資訊將顯示在這裡。</p>
      </div>
      <div className="flex justify-center">
        <img 
          src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop" 
          alt="Demand Forecast" 
          className="rounded-lg shadow-lg max-w-full h-auto"
        />
      </div>
    </div>
  );
  
  // Render capacity tab
  const renderCapacityTab = () => (
    <div className="p-6">
      <div className="bg-gray-50 p-4 rounded-lg mb-4">
        <p className="text-gray-700">此計劃的產能規劃資訊將顯示在這裡。</p>
      </div>
      <div className="flex justify-center">
        <img 
          src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=400&fit=crop" 
          alt="Capacity Planning" 
          className="rounded-lg shadow-lg max-w-full h-auto"
        />
      </div>
    </div>
  );
  
  // Render logs tab
  const renderLogsTab = () => (
    <div className="p-6">
      <div className="space-y-4">
        <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
          <Activity className="h-5 w-5 text-blue-500 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-gray-900">計劃已啟動</p>
            <p className="text-xs text-gray-500">2023-05-15 09:30:00 | 系統</p>
          </div>
        </div>
        
        <div className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
          <RefreshCw className="h-5 w-5 text-green-500 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-gray-900">物料需求已更新</p>
            <p className="text-xs text-gray-500">2023-05-16 14:22:10 | 系統</p>
          </div>
        </div>
        
        <div className="flex items-start space-x-3 p-3 bg-yellow-50 rounded-lg">
          <Users className="h-5 w-5 text-yellow-500 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-gray-900">採購建議已生成</p>
            <p className="text-xs text-gray-500">2023-05-17 11:05:33 | 系統</p>
          </div>
        </div>
      </div>
    </div>
  );
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">MRP計劃詳情</h2>
        <button 
          onClick={() => setActiveView('plans')} 
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex items-center"
        >
          <ArrowRight className="w-4 h-4 mr-2" />
          返回計劃列表
        </button>
      </div>
      
      {/* Plan Header */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center space-y-4 md:space-y-0">
            <div>
              <h3 className="text-lg font-medium text-gray-900">{plan.name}</h3>
              <span 
                className="mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full" 
                style={{ backgroundColor: `${status.color}20`, color: status.color }}
              >
                {status.name}
              </span>
            </div>
            
            <div className="flex flex-col items-end">
              <p className="text-sm text-gray-500">規劃期間: {plan.planning_period}</p>
              <p className="text-sm text-gray-500">建立日期: {new Date(plan.created_at).toLocaleDateString()}</p>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Plan Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-2 rounded-full mr-3">
                  <Calendar className="h-5 w-5 text-blue-600" />
                </div>
                <h4 className="font-medium text-gray-900">計劃資訊</h4>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-600">描述: {plan.description}</p>
                <p className="text-sm text-gray-600">開始日期: {plan.start_date}</p>
                <p className="text-sm text-gray-600">結束日期: {plan.end_date}</p>
                <p className="text-sm text-gray-600">負責人: {plan.responsible_person}</p>
              </div>
            </div>
            
            {/* Plan Image */}
            <div className="rounded-lg overflow-hidden shadow-sm">
              <img 
                src={plan.image || "https://images.unsplash.com/photo-1664575602554-2087b04935a5?w=800&h=400&fit=crop"} 
                alt="MRP Plan" 
                className="w-full h-48 object-cover"
              />
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex flex-wrap gap-2">
          <button className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors flex items-center">
            <RefreshCw className="w-4 h-4 mr-1" />
            更新計劃
          </button>
          <button className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50 transition-colors flex items-center">
            <Download className="w-4 h-4 mr-1" />
            匯出數據
          </button>
          <button className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50 transition-colors flex items-center">
            <Printer className="w-4 h-4 mr-1" />
            列印報表
          </button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('items')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'items' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              <Package className="w-4 h-4 inline mr-2" />
              物料項目
            </button>
            <button
              onClick={() => setActiveTab('forecast')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'forecast' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              需求預測
            </button>
            <button
              onClick={() => setActiveTab('capacity')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'capacity' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              <Layers className="w-4 h-4 inline mr-2" />
              產能規劃
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'logs' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              <Activity className="w-4 h-4 inline mr-2" />
              執行日誌
            </button>
          </nav>
        </div>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default MRPPlanDetail;
